/**
 * Path-encoded listing state for /venues/:type/... and /vendors/...
 * (avoids query strings for these sections).
 */

function safeDecode(s) {
  try {
    return decodeURIComponent(s.replace(/\+/g, " "));
  } catch {
    return s;
  }
}

function enc(s) {
  return encodeURIComponent(s);
}

export function getListingCityFromPathname(pathname) {
  const m = pathname.match(/\/city\/([^/]+)/);
  return m ? safeDecode(m[1]) : "";
}

/** /blog with optional ?city= */
export function buildBlogPath(city) {
  const c = (city || "").trim();
  if (!c) return "/blog";
  return `/blog?city=${encodeURIComponent(c)}`;
}

/** Homepage selected city: `/city/Mumbai` (not venues/vendors listing segments). */
export function buildHomeCityPath(city) {
  const c = (city || "").trim();
  if (!c) return "/";
  return `/city/${encodeURIComponent(c)}`;
}

function parseKeyValueSplat(splat, schema) {
  const segs = (splat || "").split("/").filter(Boolean);
  const out = { ...schema.defaults };
  for (let i = 0; i < segs.length; ) {
    const key = segs[i];
    if (key === "top") {
      out.hasTopRank = true;
      i += 1;
      continue;
    }
    const val = segs[i + 1];
    if (val === undefined) break;
    const d = safeDecode(val);
    i += 2;
    switch (key) {
      case "city":
        out.city = d;
        break;
      case "page":
        out.page = d;
        break;
      case "search":
        out.searchQuery = d;
        break;
      case "pmin":
        out.priceMin = d;
        break;
      case "pmax":
        out.priceMax = d;
        break;
      case "vfilter":
        out.venueTypeFilter = d;
        break;
      case "trank":
        out.topRankParam = d;
        break;
      case "category":
        out.category = d;
        break;
      case "exp":
        out.listExperienceMin = d;
        break;
      case "radius":
        out.listServiceRadius = d;
        break;
      case "travel":
        out.listWillingToTravel = d;
        break;
      default:
        break;
    }
  }
  return out;
}

const VENUES_DEFAULTS = {
  city: "",
  page: "1",
  searchQuery: "",
  priceMin: "",
  priceMax: "",
  venueTypeFilter: "",
  hasTopRank: false,
  topRankParam: "",
};

export function parseVenuesListingSplat(splat) {
  return parseKeyValueSplat(splat, { defaults: VENUES_DEFAULTS });
}

export function buildVenuesListingPath(type, s) {
  const t = (type || "all").trim();
  const tail = [];
  if (s.city) tail.push("city", enc(s.city.trim()));
  if (s.page && String(s.page) !== "1") tail.push("page", String(s.page));
  if (s.searchQuery) tail.push("search", enc(s.searchQuery.trim()));
  if (s.priceMin) tail.push("pmin", enc(String(s.priceMin)));
  if (s.priceMax) tail.push("pmax", enc(String(s.priceMax)));
  if (s.venueTypeFilter) tail.push("vfilter", enc(s.venueTypeFilter.trim()));
  if (s.hasTopRank) tail.push("top");
  if (s.topRankParam) tail.push("trank", enc(s.topRankParam));
  const base = `/venues/${encodeURIComponent(t)}`;
  return tail.length ? `${base}/${tail.join("/")}` : base;
}

export function legacySearchParamsToVenuesState(searchParams) {
  const sp =
    typeof searchParams === "string"
      ? new URLSearchParams(searchParams)
      : searchParams;
  return {
    city: sp.get("city") || "",
    page: sp.get("page") || "1",
    searchQuery: sp.get("search") || "",
    priceMin: sp.get("priceMin") || "",
    priceMax: sp.get("priceMax") || "",
    venueTypeFilter: sp.get("venueType") || "",
    hasTopRank: sp.has("topRank"),
    topRankParam: sp.get("topRank") || "",
  };
}

const VENDORS_DEFAULTS = {
  city: "",
  category: "",
  page: "1",
  listSearchQuery: "",
  listPriceMin: "",
  listPriceMax: "",
  listExperienceMin: "",
  listServiceRadius: "",
  listWillingToTravel: "",
  hasTopRank: false,
  topRankParam: "",
};

export function parseVendorsListingSplat(splat) {
  return parseKeyValueSplat(splat, { defaults: VENDORS_DEFAULTS });
}

export function buildVendorsListingPath(s) {
  const tail = [];
  if (s.city) tail.push("city", enc(s.city.trim()));
  if (s.category) tail.push("category", enc(String(s.category).trim().toLowerCase()));
  if (s.page && String(s.page) !== "1") tail.push("page", String(s.page));
  if (s.listSearchQuery) tail.push("search", enc(s.listSearchQuery.trim()));
  if (s.listPriceMin) tail.push("pmin", enc(String(s.listPriceMin)));
  if (s.listPriceMax) tail.push("pmax", enc(String(s.listPriceMax)));
  if (s.listExperienceMin)
    tail.push("exp", enc(String(s.listExperienceMin)));
  if (s.listServiceRadius)
    tail.push("radius", enc(String(s.listServiceRadius)));
  if (s.listWillingToTravel)
    tail.push("travel", enc(String(s.listWillingToTravel)));
  if (s.hasTopRank) tail.push("top");
  if (s.topRankParam) tail.push("trank", enc(s.topRankParam));
  const base = "/vendors";
  return tail.length ? `${base}/${tail.join("/")}` : base;
}

export function legacySearchParamsToVendorsState(searchParams) {
  const sp =
    typeof searchParams === "string"
      ? new URLSearchParams(searchParams)
      : searchParams;
  return {
    city: sp.get("city") || "",
    category: sp.get("category") || "",
    page: sp.get("page") || "1",
    listSearchQuery: sp.get("search") || "",
    listPriceMin: sp.get("priceMin") || "",
    listPriceMax: sp.get("priceMax") || "",
    listExperienceMin: sp.get("experienceMin") || "",
    listServiceRadius: sp.get("serviceRadius") || "",
    listWillingToTravel: sp.get("willingToTravel") || "",
    hasTopRank: sp.has("topRank"),
    topRankParam: sp.get("topRank") || "",
  };
}

/** /venues/:type and optional /city/... tail */
export function parseVenuesPathname(pathname) {
  const m = pathname.match(/^\/venues\/([^/]+)(?:\/(.*))?$/);
  if (!m) return { type: "all", rest: "" };
  return { type: m[1], rest: m[2] || "" };
}

/**
 * /vendors, /vendors/vendor/:id, or /vendors/city/...
 * @returns {{ vendorId: string, listingRest: string }}
 */
export function parseVendorsPathname(pathname) {
  if (!pathname.startsWith("/vendors")) {
    return { vendorId: "", listingRest: "" };
  }
  const after = pathname.slice("/vendors".length).replace(/^\//, "");
  if (!after) return { vendorId: "", listingRest: "" };
  if (after.startsWith("vendor/")) {
    const sub = after.slice("vendor/".length);
    const i = sub.indexOf("/");
    const id = i === -1 ? sub : sub.slice(0, i);
    const listingRest = i === -1 ? "" : sub.slice(i + 1);
    return { vendorId: id, listingRest };
  }
  return { vendorId: "", listingRest: after };
}

export function buildVendorDetailPath(vendorId) {
  return `/vendors/vendor/${encodeURIComponent(String(vendorId))}`;
}
